import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const TIER_COLOR = ["#006747", "#2a6f8a", "#c0612f"];

export function Schedule() {
  const { data, isLoading, error } = useQuery({ queryKey: ["schedule"], queryFn: api.schedule });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Schedule & on-call</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>Tiers, shifts, coverage gaps, and the on-call rotation. Offers only route to reps on shift.</div>
      </div>

      {/* Tiers */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 }}>
        {data.tiers.map((t, i) => (
          <div key={t.tier} style={{ background: "#fff", borderRadius: 3, boxShadow: `inset 0 0 0 1px ${TIER_COLOR[i]}33`, padding: "15px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#fff", background: TIER_COLOR[i], borderRadius: 5, padding: "2px 8px", fontFamily: "'Space Mono',monospace" }}>{t.tier}</span>
              <span style={{ fontSize: 12, color: "#8a8e88" }}>{t.coverage}</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, marginTop: 8 }}>{t.role}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
              {t.reps.map((r) => <span key={r} style={{ fontSize: 11.5, color: "#4a504a", background: "#f6f5f1", border: "1px solid #e7e5df", borderRadius: 999, padding: "2px 8px" }}>{r}</span>)}
            </div>
            <div style={{ fontSize: 11.5, color: "#8a8e88", marginTop: 8 }}>Escalates when: {t.escalateWhen}</div>
          </div>
        ))}
      </div>

      {/* Weekly grid */}
      <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", overflow: "hidden" }}>
        <div style={{ padding: "15px 18px 10px", fontSize: 15, fontWeight: 600 }}>This week</div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: `160px repeat(${data.days.length}, minmax(96px,1fr))`, minWidth: 640 }}>
            <div style={{ padding: "8px 18px", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "#a8aca6", borderTop: "1px solid #f4f3ee" }}>Rep</div>
            {data.days.map((d) => <div key={d} style={{ padding: "8px 10px", fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", color: "#a8aca6", borderTop: "1px solid #f4f3ee", textAlign: "center" }}>{d}</div>)}
            {data.shifts.map(([name, tier, cells]) => (
              <div key={name} style={{ display: "contents" }}>
                <div style={{ padding: "9px 18px", fontSize: 12.5, fontWeight: 600, borderTop: "1px solid #f4f3ee", display: "flex", alignItems: "center", gap: 7 }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: TIER_COLOR[tier - 1] }} />{name}
                </div>
                {cells.map((cell, ci) => (
                  <div key={ci} style={{ padding: "9px 8px", borderTop: "1px solid #f4f3ee", textAlign: "center" }}>
                    {cell === "Off" ? (
                      <span style={{ fontSize: 11, color: "#bcbfba" }}>Off</span>
                    ) : (
                      <span style={{ fontSize: 10.5, fontWeight: 600, color: TIER_COLOR[tier - 1], background: TIER_COLOR[tier - 1] + "14", borderRadius: 4, padding: "3px 6px", display: "inline-block", whiteSpace: "nowrap" }}>{cell.replace(/^T\d · /, "")}</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 16 }}>
        <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(217,119,87,.45)", padding: "15px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Icon name="alert-triangle" size={15} color="#c0612f" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Coverage gaps</h2>
          </div>
          {data.gaps.map((g, i) => <div key={i} style={{ fontSize: 12.5, color: "#7a4a22", padding: "5px 0", borderTop: i ? "1px solid #f4f3ee" : "none" }}>{g}</div>)}
        </div>
        <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Icon name="phone" size={15} color="#006747" /><h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>On-call</h2>
          </div>
          {data.onCall.map(([when, who, note], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderTop: i ? "1px solid #f4f3ee" : "none", fontSize: 12.5 }}>
              <span><span style={{ fontWeight: 600 }}>{when}</span> · {who}</span>
              <span style={{ color: "#8a8e88", textAlign: "right" }}>{note}</span>
            </div>
          ))}
        </div>
      </div>
    </Page>
  );
}
